import React, { useRef, useState, useEffect } from 'react';
import EmailEditor, { EditorRef } from 'react-email-editor';
import { TextField, CircularProgress, Box } from '@mui/material';
import BaseModal from '../../../utils/BaseModal';
import api from '../../../../api/axios';
import { toast } from 'react-toastify';
import { DEFAULT_NEWSLETTER_DESIGN } from './templates/newsletterTemplate';

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editTemplate?: any;
  draft?: any;
  onDraftChange?: (design: any) => void;
};

const CreateTemplateModal: React.FC<Props> = ({
                                                open,
                                                onClose,
                                                onSuccess,
                                                editTemplate,
                                                draft,
                                                onDraftChange
                                              }) => {
  const emailEditorRef = useRef<EditorRef>(null);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [editorLoaded, setEditorLoaded] = useState(false);
  const lastLoadedId = useRef<string | null>(null);

  // Handle Name and Design loading when modal opens
  useEffect(() => {
    if (!open) return;

    if (editTemplate) {
      setName(editTemplate.name);
      // Only load if it's a different template than before
      if (lastLoadedId.current !== `edit-${editTemplate.id}`) {
        loadDesignToEditor(JSON.parse(editTemplate.designJson));
        lastLoadedId.current = `edit-${editTemplate.id}`;
      }
    } else if (draft) {
      // Keep name if we have a draft
      if (lastLoadedId.current !== 'draft') {
        loadDesignToEditor(draft);
        lastLoadedId.current = 'draft';
      }
    } else {
      setName('');
      if (lastLoadedId.current !== 'new') {
        loadDesignToEditor(DEFAULT_NEWSLETTER_DESIGN);
        lastLoadedId.current = 'new';
      }
    }
  }, [open, editTemplate]);

  const loadDesignToEditor = (design: any) => {
    const unlayer = emailEditorRef.current?.editor;
    if (unlayer && editorLoaded) {
      unlayer.loadDesign(design);
    }
  };

  const onEditorReady = () => {
    const unlayer = emailEditorRef.current?.editor;
    if (unlayer) {
      // Initial load
      const design = draft || (editTemplate ? JSON.parse(editTemplate.designJson) : DEFAULT_NEWSLETTER_DESIGN);
      unlayer.loadDesign(design);

      // Save changes to draft on every change
      unlayer.addEventListener('design:updated', () => {
        unlayer.saveDesign((latestDesign) => {
          onDraftChange?.(latestDesign);
        });
      });

      setEditorLoaded(true);
    }
  };

  const handleSaveTemplate = () => {
    if (!name) {
      toast.error("Please enter a template name");
      return;
    }

    const unlayer = emailEditorRef.current?.editor;
    if (!unlayer) return;

    unlayer.saveDesign(async (design) => {
      setLoading(true);
      try {
        const payload = {
          name,
          designJson: JSON.stringify(design),
        };

        if (editTemplate) {
          await api.put(`/system/newsletter/templates/${editTemplate.id}`, payload);
        } else {
          await api.post('/system/newsletter/templates', payload);
        }

        toast.success("Template saved successfully");
        lastLoadedId.current = null; // Reset for next time
        onSuccess();
        onClose();
      } catch (e) {
        toast.error("Failed to save template");
      } finally {
        setLoading(false);
      }
    });
  };

  return (
      <BaseModal
          open={open}
          title={editTemplate ? "Edit Template" : "Create New Template"}
          onAbort={onClose}
          onSubmit={handleSaveTemplate}
          submitText={loading ? "Saving..." : "Save Template"}
          submitDisabled={!editorLoaded || loading}
          maxWidth="xl"
          disablePaddingTop={true}
          // Important: If your BaseModal supports it, ensure it uses keepMounted
          // or does not conditionally render children.
      >
        <Box className="flex flex-col gap-4 h-[78vh]">
          <TextField
              fullWidth
              label="Template Name"
              variant="filled"
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{
                '& .MuiInputBase-root': { borderRadius: 0, backgroundColor: 'rgba(0,0,0,0.2)' },
                input: { color: 'white' }
              }}
          />
          <div className="flex-1 relative border border-white/10 bg-[#1a1a1a]">
            {!editorLoaded && (
                <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-[#1a1a1a]">
                  <CircularProgress size={40} />
                </div>
            )}
            <EmailEditor
                ref={emailEditorRef}
                onReady={onEditorReady}
                minHeight="100%"
                options={{
                  appearance: {
                    theme: 'dark',
                    panels: { tools: { dock: 'left' } }
                  },
                  features: { stockImages: true },
                  tools: {
                    image: { enabled: true },
                    button: { enabled: true },
                    text: { enabled: true },
                    divider: { enabled: true },
                    social: { enabled: true },
                    spacer: { enabled: true }
                  },
                  locale: 'en-US',
                }}
            />
          </div>
        </Box>
      </BaseModal>
  );
};

export default CreateTemplateModal;